import { Box, EmptyState as ChakraEmptyState, VStack } from '@chakra-ui/react';
import * as React from 'react';

export interface EmptyStateProps extends ChakraEmptyState.RootProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  /**
   * Drop the block border and fill while retaining the bordered icon marker.
   * Use when the empty state already sits inside a surface where another
   * boxed block would be redundant. Defaults to `false`.
   */
  bare?: boolean;
  /**
   * Width of the centred column. Defaults to a narrow 460px; a state that
   * lays out several cards (a first visit's steps) asks for a wider one.
   */
  contentMaxW?: string;
  /**
   * `success` paints the icon on a soft green tile, for an all-clear state
   * ("Tout est à jour") rather than an absence. Defaults to `neutral`.
   */
  iconTone?: 'neutral' | 'success';
}

const ICON_TONE = {
  success: {
    rounded: '14px',
    bg: 'green.subtle',
    borderWidth: '0',
    color: 'green.fg',
  },
} as const;

/**
 * Shared empty-state block, rebrand-styled (light `sidebarBg` fill, subtle solid
 * hairline, white icon circle, heading/body fonts, charcoal ink). Title +
 * optional helper + optional icon, with `children` acting as an optional
 * action/CTA slot. No shadows and no dashed borders: solid hairline only.
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState(props, ref) {
    const {
      title,
      description,
      icon,
      bare = false,
      contentMaxW = '460px',
      iconTone = 'neutral',
      children,
      ...rest
    } = props;
    return (
      <ChakraEmptyState.Root
        ref={ref}
        w="full"
        py={12}
        px={4}
        borderWidth={bare ? '0' : '1px'}
        borderStyle="solid"
        borderColor="border.subtle"
        rounded="lg"
        bg={bare ? 'transparent' : 'sidebarBg/50'}
        {...rest}
      >
        <ChakraEmptyState.Content gap={0} maxW={contentMaxW} mx="auto" textAlign="center">
          {icon && (
            <ChakraEmptyState.Indicator mb="14px">
              <Box
                display="grid"
                placeItems="center"
                boxSize="48px"
                borderColor="hairline.field"
                css={{ '& svg': { width: '21px', height: '21px' } }}
                {...(iconTone === 'success'
                  ? ICON_TONE.success
                  : {
                      rounded: 'full',
                      bg: bare ? 'transparent' : 'panelBg',
                      borderWidth: '1px',
                      color: 'navInactive',
                    })}
              >
                {icon}
              </Box>
            </ChakraEmptyState.Indicator>
          )}
          <VStack gap="6px">
            <ChakraEmptyState.Title
              fontFamily="heading"
              fontSize="16px"
              fontWeight="semibold"
              letterSpacing="-0.01em"
              color="primary.900"
              lineHeight="1.3"
            >
              {title}
            </ChakraEmptyState.Title>
            {description && (
              <ChakraEmptyState.Description
                maxW="320px"
                fontFamily="body"
                fontSize="12.5px"
                fontWeight="light"
                color="inkMuted"
                lineHeight="1.5"
              >
                {description}
              </ChakraEmptyState.Description>
            )}
          </VStack>
          {children && <Box mt="16px">{children}</Box>}
        </ChakraEmptyState.Content>
      </ChakraEmptyState.Root>
    );
  }
);
